<template>
    <b-modal :id="id" :title="title" @show="onShow" @ok="onOk">
        <p v-if="description" class="text-muted small mb-3">{{ description }}</p>

        <template v-for="group in groupedItems">
            <h6 v-if="group.name" :key="`${group.name}-h`" class="text-muted small mb-2">{{ group.name }}</h6>
            <div :key="`${group.name || 'default'}-grid`" class="visibility-grid"
                :class="{ 'mb-3': groupedItems.length > 1 }">
                <b-form-checkbox v-for="item in group.items" :key="item.key"
                    :checked="isChecked(item.key)"
                    @change="toggle(item.key, $event)">
                    {{ item.label }}
                </b-form-checkbox>
            </div>
        </template>

        <template #modal-footer="{ ok, cancel }">
            <b-button v-if="defaults" variant="link" size="sm" class="mr-auto p-0"
                @click="resetToDefaults">
                Reset to defaults
            </b-button>
            <b-button variant="link" @click="cancel()">Cancel</b-button>
            <b-button variant="primary" @click="ok()">Save</b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Model } from 'vue-property-decorator';

export interface VisibilityItem {
    key: string;
    label: string;
    group?: string;
}

@Component({ name: 'QueueVisibilityModal' })
export default class QueueVisibilityModal extends Vue {
    @Prop({ required: true }) readonly id!: string;
    @Prop({ required: true }) readonly title!: string;
    @Prop({ default: '' }) readonly description!: string;
    @Prop({ required: true }) readonly items!: VisibilityItem[];
    @Prop({ default: null }) readonly defaults!: { [key: string]: boolean } | null;

    @Model('change', { type: Object, required: true })
    readonly value!: { [key: string]: boolean };

    // Draft copy mirrors v-model while the modal is open so Cancel discards edits;
    // we only emit 'change' (and 'save') on the OK button. b-form-checkbox binds to
    // draft[key] via isChecked/toggle so the parent's v-model isn't touched until Save.
    private draft: { [key: string]: boolean } = {};

    get groupedItems() {
        const groups: { name: string; items: VisibilityItem[] }[] = [];
        const byName = new Map<string, VisibilityItem[]>();
        for (const item of this.items) {
            const name = item.group || '';
            if (!byName.has(name)) byName.set(name, []);
            byName.get(name)!.push(item);
        }
        for (const [name, items] of byName.entries()) {
            groups.push({ name, items });
        }
        return groups;
    }

    isChecked(key: string): boolean {
        return this.draft[key] !== false;
    }

    toggle(key: string, checked: boolean) {
        Vue.set(this.draft, key, checked);
    }

    resetToDefaults() {
        if (!this.defaults) return;
        this.draft = { ...this.defaults };
    }

    onShow() {
        // Snapshot current v-model whenever the modal opens so edits start fresh.
        this.draft = { ...this.value };
    }

    onOk() {
        this.$emit('change', { ...this.draft });
        this.$emit('save');
    }
}
</script>

<style scoped>
.visibility-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    row-gap: 8px;
    column-gap: 16px;
}
</style>
